// AllProjects.jsx
import React, { useState } from "react";
import {
  Table,
  Button,
  Modal,
  Card,
  Form,
  Row,
  Col,
  Spinner,
  Container,
  Accordion,
  Dropdown,
  Alert,
} from "react-bootstrap";
import { FaEye, FaCheck, FaTimes, FaUsers, FaPlus, FaEllipsisV, FaTrash } from "react-icons/fa";
import { useProjectContext } from "../../contextProviders/projectContext";
import { useUser } from "../../contextProviders/userContext";
import NotificationIcon from "../../components/Notification";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import CreateProjectModal from "../../components/CreateProjectModal";
import { useNavigate } from "react-router-dom";

const interests = [
  "Wits Food Bank",
  "Masidleni Daily Meals",
  "Wits Food Gardens",
  "Wits Student Communal Kitchen",
  "Community Food Programmes",
  "Developing Food Gardens",
  "Urban Waste Management",
  "Reading Clubs",
  "In-person tutoring for high school and primary school learners",
  "Online tutoring for high school and primary school learners",
  "After school assistance programmes",
  "Health and allied assessments",
  "Sports development",
  "Arts and Cultural Development Programmes",
  "Animal welfare",
  "Befriending the elderly",
  "Clothing drive",
  "Hygiene drive",
  "Food drives",
  "Social Justice Campaigns",
  "Others",
];

const sdgOptions = [
  "No Poverty",
  "Zero Hunger",
  "Good Health and Well-being",
  "Quality Education",
  "Gender Equality",
  "Clean Water and Sanitation",
  "Affordable and Clean Energy",
  "Decent Work and Economic Growth",
  "Industry, Innovation and Infrastructure",
  "Reduced Inequalities",
  "Sustainable Cities and Communities",
  "Responsible Consumption and Production",
  "Climate Action",
  "Life Below Water",
  "Life On Land",
  "Peace, Justice and Strong Institutions",
  "Partnerships for the Goals",
];

// Custom 3-dot dropdown toggle component
const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <span
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    style={{ 
      cursor: "pointer", 
      padding: "6px 12px", 
      display: "inline-block", 
      color: "var(--color-text-secondary)",
      borderRadius: "var(--radius-sm)",
      transition: "all var(--transition)"
    }}
    className="hover-bg-light"
  >
    <FaEllipsisV />
    {children}
  </span>
));

const AdminProjectsTable = () => {
  const {
    projects,
    loading,
    error,
    updateProject,
    fetchProjects,
    deleteProject,
  } = useProjectContext();
  const { user } = useUser();
  const navigate = useNavigate();
  const [showDescription, setShowDescription] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [selectedProject] = useState(null);
  const [participantDetails] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [organiserTypeFilter, setOrganiserTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // State for project creation using CreateProjectModal
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Delete Project State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [actionFeedback, setActionFeedback] = useState({ type: "", message: "" });

  const handleDeleteProjectClick = (projectId, projectName) => {
    setProjectToDelete({ id: projectId, name: projectName });
    setDeleteConfirmInput("");
    setShowDeleteConfirm(true);
    setActionFeedback({ type: "", message: "" });
  };

  const handleDeleteProjectSubmit = async (e) => {
    e.preventDefault();
    if (!projectToDelete || deleteConfirmInput !== "DELETE") return;
    
    setDeleteLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      await deleteProject(projectToDelete.id);
      setActionFeedback({
        type: "success",
        message: `Project "${projectToDelete.name}" successfully deleted!`,
      });
      setTimeout(() => {
        setShowDeleteConfirm(false);
        setProjectToDelete(null);
        setDeleteConfirmInput("");
        setActionFeedback({ type: "", message: "" });
      }, 1500);
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.message || "Failed to delete project. Please try again.",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleProjectCreated = () => {
    fetchProjects(); // Refresh projects list after creation
  };

  const handleApproveReject = async (projectId, isApproved) => {
    try {
      let feedback = "";
      if (!isApproved) {
        const userInput = prompt("Enter a reason/feedback for denying this project proposal:");
        if (userInput === null) return; // User clicked Cancel
        feedback = userInput;
      }
      await updateProject(projectId, { approved: isApproved, feedback });
      fetchProjects();
    } catch (error) {
      console.error("Error updating project:", error);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const filteredProjects = projects.filter((project) => {
    const nameMatch = project.name
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const organiserTypeMatch = organiserTypeFilter
      ? project.organiserType === organiserTypeFilter
      : true;
    const statusMatch = statusFilter
      ? (statusFilter === "Approved" && project.approved === true) ||
        (statusFilter === "Denied" && project.approved === false) ||
        (statusFilter === "Pending" && project.approved === null)
      : true;
    return nameMatch && organiserTypeMatch && statusMatch;
  });

  // Pagination Calculations
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const activePage = currentPage > totalPages ? 1 : currentPage;
  const paginatedProjects = filteredProjects.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  const uniqueOrganiserTypes = [
    ...new Set(projects.map((project) => project.organiserType)),
  ];

  const cardStyle = {
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderTop: "4px solid var(--color-primary)",
    boxShadow: "var(--shadow-sm)",
    borderRadius: "var(--radius-lg)",
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <div className="error-message">{error}</div>
      </Container>
    );
  }

  return (
    <div
      className="d-flex flex-column flex-lg-row"
      style={{
        maxWidth: "100vw",
        overflowX: "hidden",
        backgroundColor: "var(--color-bg)"
      }}>
      <Sidebar />

      <Container
        fluid
        className="py-3 px-4"
        style={{ minHeight: "100vh", maxHeight: "100vh", overflowY: "scroll" }}>
        <TopNavBar />

        {/* Greeting Banner */}
        <Row className="align-items-center justify-content-between mt-3">
          <Col xs={10} md={6}>
            <div className="d-flex flex-column text-start">
              <h3 className="fw-bold mb-1" style={{ color: "var(--color-primary)" }}>
                {getGreeting()}, {user?.name || "Admin"}
              </h3>
              <p className="mb-0 text-muted" style={{ fontSize: "0.95rem" }}>
                Take a look at all registered volunteering programs.
              </p>
            </div>
          </Col>
          <Col
            xs={2}
            md={6}
            className="d-flex align-items-center justify-content-end mt-3 mt-md-0">
            <div className="ms-2">
              <NotificationIcon />
            </div>
          </Col>
        </Row>

        <hr style={{ color: "var(--color-border)", width: "100%", margin: "20px 0" }} />

        {/* Create Project Button Bar */}
        <Row className="mb-3">
          <Col>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="float-end fw-bold d-flex align-items-center gap-2 btn-wcco-primary shadow-sm"
              style={{ padding: "10px 20px" }}>
              <FaPlus /> Create Project
            </Button>
          </Col>
        </Row>

        {/* Projects Listing Card */}
        <Card className="mb-4 shadow-sm border-0" style={cardStyle}>
          <Card.Body className="p-4">
            {/* Filters */}
            <Row className="mb-4 g-3">
              <Col md={4}>
                <Form.Control
                  type="text"
                  placeholder="Search by project name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}
                />
              </Col>
              <Col md={4}>
                <Form.Select
                  value={organiserTypeFilter}
                  onChange={(e) => setOrganiserTypeFilter(e.target.value)}
                  style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}>
                  <option value="">All Organiser Types</option>
                  {uniqueOrganiserTypes.map((type) => (
                     <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <Form.Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}>
                  <option value="">All Statuses</option>
                  <option value="Approved">Approved</option>
                  <option value="Denied">Denied</option>
                  <option value="Pending">Pending</option>
                </Form.Select>
              </Col>
            </Row>

            {filteredProjects.length > 0 ? (
              <div className="table-responsive">
                <Table hover align="middle" className="mb-0">
                  <thead style={{ backgroundColor: "rgba(27, 58, 135, 0.05)", borderBottom: "2px solid var(--color-border)" }}>
                    <tr>
                      <th className="text-start py-3" style={{ borderTopLeftRadius: "var(--radius-md)", color: "var(--color-primary)", fontWeight: "700" }}>Project Name</th>
                      <th className="text-start py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Organiser</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Start Date</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>End Date</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Participants</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Status</th>
                      <th className="text-end py-3" style={{ borderTopRightRadius: "var(--radius-md)", paddingRight: "20px", color: "var(--color-primary)", fontWeight: "700" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedProjects.map((project) => (
                      <tr 
                        key={project._id}
                        onClick={() => navigate(`/admin/projects/${project._id}`)}
                        style={{ cursor: "pointer", transition: "background-color var(--transition)" }}
                        className="align-middle"
                      >
                        <td className="text-start fw-bold py-3" style={{ color: "var(--color-text)" }}>
                          {project.name}
                          {project.amendments?.some((item) => item.status === "pending") && (
                            <span className="badge-gold ms-2 px-2 py-1" style={{ fontSize: "0.7rem", borderRadius: "10px" }}>
                              Amendment review
                            </span>
                          )}
                        </td>
                        <td className="text-start py-3">
                          <div className="fw-semibold" style={{ color: "var(--color-text)" }}>{project.organiserName}</div>
                          <div className="text-muted small" style={{ color: "var(--color-text-secondary)" }}>{project.organiserType}</div>
                        </td>
                        <td className="py-3" style={{ color: "var(--color-text-secondary)" }}>
                          {new Date(project.startDate).toLocaleDateString()}
                        </td>
                        <td className="py-3" style={{ color: "var(--color-text-secondary)" }}>
                          {project.endDate
                            ? new Date(project.endDate).toLocaleDateString()
                            : "N/A"}
                        </td>
                        <td className="py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="d-flex align-items-center justify-content-center">
                            <span className="badge-primary px-2 py-1" style={{ fontSize: "0.85rem", fontWeight: "600" }}>
                              {project.participants.length}
                            </span>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 ms-2 text-decoration-none"
                              style={{ color: "var(--color-primary)", fontWeight: "600" }}
                              onClick={() => navigate(`/admin/projects/${project._id}`, { state: { activeTab: "participants" } })}>
                              <FaUsers size={14} className="me-1" /> View
                            </Button>
                          </div>
                        </td>
                        <td className="py-3">
                          <span
                            className={`px-3 py-1.5 fw-bold ${
                              project.approved
                                ? "badge-teal"
                                : project.approved === false
                                ? "badge-red"
                                : "badge-gold"
                            }`}
                            style={{ display: "inline-block", borderRadius: "20px", fontSize: "0.8rem" }}>
                            {project.approved
                              ? "Approved"
                              : project.approved === false
                              ? "Denied"
                              : "Pending"}
                          </span>
                        </td>
                        <td className="text-end py-3" style={{ paddingRight: "20px" }} onClick={(e) => e.stopPropagation()}>
                          <Dropdown align="end">
                            <Dropdown.Toggle as={CustomToggle} id={`dropdown-${project._id}`} />
                            <Dropdown.Menu className="shadow border-0 py-2" style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-md)" }}>
                              <Dropdown.Item 
                                className="py-2"
                                onClick={() => navigate(`/admin/projects/${project._id}`)}
                                style={{ color: "var(--color-text)" }}
                              >
                                <FaEye className="me-2" style={{ color: "var(--color-primary)" }} /> View Details
                              </Dropdown.Item>
                              <Dropdown.Item 
                                className="py-2"
                                onClick={() => handleApproveReject(project._id, true)}
                                disabled={project.approved === true}
                                style={{ color: "var(--color-text)" }}
                              >
                                <FaCheck className="me-2" style={{ color: "var(--color-teal)" }} /> Approve Project
                              </Dropdown.Item>
                              <Dropdown.Item 
                                className="py-2"
                                onClick={() => handleApproveReject(project._id, false)}
                                disabled={project.approved === false}
                                style={{ color: "var(--color-text)" }}
                              >
                                <FaTimes className="me-2" style={{ color: "var(--color-red)" }} /> Deny Project
                              </Dropdown.Item>
                              <Dropdown.Item 
                                className="py-2 text-danger"
                                onClick={() => handleDeleteProjectClick(project._id, project.name)}
                              >
                                <FaTrash className="me-2" style={{ color: "var(--color-red)" }} /> Delete Project
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                    <div className="text-muted small">
                      Showing <strong>{(activePage - 1) * itemsPerPage + 1}</strong> to{" "}
                      <strong>{Math.min(activePage * itemsPerPage, filteredProjects.length)}</strong> of{" "}
                      <strong>{filteredProjects.length}</strong> projects
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        variant="light"
                        size="sm"
                        disabled={activePage === 1}
                        onClick={() => setCurrentPage(activePage - 1)}
                        style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                      >
                        Previous
                      </Button>
                      {[...Array(totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        const isActive = pageNum === activePage;
                        return (
                          <Button
                            key={pageNum}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            style={{
                              backgroundColor: isActive ? "var(--color-primary)" : "var(--color-surface)",
                              color: isActive ? "#fff" : "var(--color-text-secondary)",
                              border: `1px solid ${isActive ? "var(--color-primary)" : "var(--color-border)"}`,
                              borderRadius: "var(--radius-sm)",
                              fontWeight: "600",
                              minWidth: "32px"
                            }}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      <Button
                        variant="light"
                        size="sm"
                        disabled={activePage === totalPages}
                        onClick={() => setCurrentPage(activePage + 1)}
                        style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No volunteering programs found.</p>
              </div>
            )}
          </Card.Body>
        </Card>

        {selectedProject && (
          <Modal
            show={showDescription}
            onHide={() => setShowDescription(false)}
            size="lg">
            <Modal.Header closeButton>
              <Modal.Title style={{ color: "#006089" }}>
                {selectedProject.name} - Project Details
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Card className="mb-4" style={cardStyle}>
                <Card.Body>
                  <h4>Project Information</h4>
                  <Table hover size="sm">
                    <tbody>
                      <tr>
                        <th style={{ backgroundColor: "#F3F4F6" }}>
                          Description
                        </th>
                        <td style={{ backgroundColor: "#F3F4F6" }}>
                          {selectedProject.description}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#F3F4F6" }}>
                          Organiser Type
                        </th>
                        <td style={{ backgroundColor: "#F3F4F6" }}>
                          {selectedProject.organiserType}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#F3F4F6" }}>
                          Created By
                        </th>
                        <td style={{ backgroundColor: "#F3F4F6" }}>
                          {selectedProject.createdBy}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#F3F4F6" }}>
                          Start Date
                        </th>
                        <td style={{ backgroundColor: "#F3F4F6" }}>
                          {new Date(
                            selectedProject.startDate
                          ).toLocaleDateString()}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#F3F4F6" }}>End Date</th>
                        <td style={{ backgroundColor: "#F3F4F6" }}>
                          {selectedProject.endDate
                            ? new Date(
                                selectedProject.endDate
                              ).toLocaleDateString()
                            : "N/A"}
                        </td>
                      </tr>
                      <tr>
                        <th style={{ backgroundColor: "#F3F4F6" }}>Status</th>
                        <td style={{ backgroundColor: "#F3F4F6" }}>
                          <span
                            className={`px-3 py-1.5 fw-bold ${
                              selectedProject.approved
                                ? "badge-teal"
                                : selectedProject.approved === false
                                ? "badge-red"
                                : "badge-gold"
                            }`}
                            style={{ display: "inline-block", borderRadius: "20px", fontSize: "0.8rem" }}>
                            {selectedProject.approved
                              ? "Approved"
                              : selectedProject.approved === false
                              ? "Denied"
                              : "Pending"}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowDescription(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {selectedProject && (
          <Modal
            show={showParticipants}
            onHide={() => setShowParticipants(false)}
            size="lg">
            <Modal.Header closeButton>
              <Modal.Title style={{ color: "#006089" }}>
                {selectedProject.name} - Participants and Logs
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Card className="mb-4" style={cardStyle}>
                <Card.Body>
                  <h4>Participant Details</h4>
                  {participantDetails.length > 0 ? (
                    <Accordion>
                      {participantDetails.map((participant, index) => (
                        <Accordion.Item
                          eventKey={index.toString()}
                          key={participant._id}>
                          <Accordion.Header>
                            {participant.student.name} - {participant.role}
                          </Accordion.Header>
                          <Accordion.Body>
                            <Table hover size="sm">
                              <tbody>
                                <tr>
                                  <th style={{ backgroundColor: "#F3F4F6" }}>
                                    Email
                                  </th>
                                  <td style={{ backgroundColor: "#F3F4F6" }}>
                                    {participant.student.email}
                                  </td>
                                </tr>
                                <tr>
                                  <th style={{ backgroundColor: "#F3F4F6" }}>
                                    Hours Logged
                                  </th>
                                  <td style={{ backgroundColor: "#F3F4F6" }}>
                                    {participant.logs.reduce(
                                      (sum, log) => sum + log.hours,
                                      0
                                    )}
                                  </td>
                                </tr>
                                <tr>
                                  <th style={{ backgroundColor: "#F3F4F6" }}>
                                    Date Joined
                                  </th>
                                  <td style={{ backgroundColor: "#F3F4F6" }}>
                                    {new Date(
                                      participant.dateJoined
                                    ).toLocaleDateString()}
                                  </td>
                                </tr>
                              </tbody>
                            </Table>
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  ) : (
                    <p>No participants found for this project.</p>
                  )}
                </Card.Body>
              </Card>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowParticipants(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        )}

        {/* Create Project Modal */}
        <CreateProjectModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          onProjectCreated={handleProjectCreated}
          interests={interests}
          sdgOptions={sdgOptions}
        />

        {/* Delete Project Confirmation Modal */}
        {projectToDelete && (
          <Modal show={showDeleteConfirm} onHide={() => { if (!deleteLoading) setShowDeleteConfirm(false); }} centered>
            <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
              <Modal.Title className="fw-bold text-danger">
                Confirm Project Deletion
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3">
              {actionFeedback.message && (
                <Alert variant={actionFeedback.type} className="py-2.5 px-3 mb-3 small text-start">
                  {actionFeedback.message}
                </Alert>
              )}
              <p className="text-start mb-3">
                Are you sure you want to permanently delete the project <strong>{projectToDelete.name}</strong>?
              </p>
              <p className="text-start text-danger mb-4 small fw-semibold">
                WARNING: This action will permanently erase this project and all associated volunteering hours, logs, and student reflections.
              </p>
              <Form onSubmit={handleDeleteProjectSubmit}>
                <Form.Group className="mb-3 text-start">
                  <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Type <strong>DELETE</strong> to confirm:</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type DELETE..."
                    value={deleteConfirmInput}
                    onChange={(e) => setDeleteConfirmInput(e.target.value)}
                    required
                    style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                  />
                </Form.Group>
                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="secondary"
                    disabled={deleteLoading}
                    style={{ borderRadius: "var(--radius-sm)" }}
                    onClick={() => { setShowDeleteConfirm(false); setProjectToDelete(null); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={deleteLoading || deleteConfirmInput !== "DELETE"}
                    style={{ backgroundColor: "var(--color-red)", color: "#fff", borderRadius: "var(--radius-sm)", border: "none" }}
                  >
                    {deleteLoading ? "Deleting..." : "Permanently Delete"}
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>
        )}
      </Container>
    </div>
  );
};

export default AdminProjectsTable;
